/**
 * LightPath Pricing API Service
 * 
 * Fetches pricing estimates from the LightPath Pricing API
 * https://lightpathpricing.netlify.app/api/pricing
 */

export interface LightPathPricingRequest {
  users: number;
  termYears: 1 | 3 | 4 | 5;
  customerName?: string;
}

export interface LightPathPaymentMilestone {
  milestone: number;
  description: string;
  percentage?: string;
  amount: number;
}

export interface LightPathPricingResponse {
  success: boolean;
  data?: {
    users: number;
    termYears: number;
    tier: {
      min: number;
      max: number | null;
      tierName: string;
      annualLicense: number | null;
    };
    monthlyLicense: number;
    annualLicense: number;
    originalAnnualLicense: number;
    discountPercentage: number;
    discountAmount: number;
    implementation: number;
    totalFirstYear: number;
    paymentSchedule: LightPathPaymentMilestone[];
    contactUs: boolean;
    customerName?: string;
  };
  error?: string;
}

const API_BASE_URL = 'https://lightpathpricing.netlify.app/api/pricing';

/**
 * Convert license term string to years number
 */
function parseTermYears(termString: string): 1 | 3 | 4 | 5 {
  const match = termString.match(/^(\d+)-year$/);
  const years = match ? parseInt(match[1]) : 1;
  
  // Only support 1, 3, 4, 5 year terms per API spec
  if (years === 3 || years === 4 || years === 5) {
    return years as 1 | 3 | 4 | 5;
  }
  
  return 1; // Default to 1 year
}

/**
 * Fetch pricing estimate from LightPath Pricing API
 */
export async function fetchLightPathPricing(
  users: number,
  termString: string,
  customerName?: string
): Promise<LightPathPricingResponse> {
  try {
    const termYears = parseTermYears(termString);
    
    const requestBody: LightPathPricingRequest = {
      users,
      termYears,
      customerName,
    };

    console.log('📊 Fetching LightPath pricing:', requestBody);

    // Get API key from environment variables
    const apiKey = import.meta.env.VITE_LIGHTPATH_API_KEY;
    
    if (!apiKey) {
      console.warn('⚠️ VITE_LIGHTPATH_API_KEY not configured');
      return {
        success: false,
        error: 'LightPath API key not configured. Please contact your administrator.',
      };
    }

    const response = await fetch(API_BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey,
      },
      body: JSON.stringify(requestBody),
    });

    const result: LightPathPricingResponse = await response.json();

    // Handle specific HTTP status codes
    if (!response.ok) {
      if (response.status === 401) {
        console.error('❌ LightPath API: Unauthorized (invalid API key)');
        return {
          success: false,
          error: 'Invalid API key. Please contact your administrator.',
        };
      }
      
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const remaining = response.headers.get('X-RateLimit-Remaining');
        console.warn(`⚠️ LightPath API: Rate limited. Retry after ${retryAfter}s. Remaining: ${remaining}`);
        return {
          success: false,
          error: `Rate limit exceeded. Please wait ${retryAfter || '60'} seconds and try again.`,
        };
      }
      
      if (response.status === 403) {
        console.error('❌ LightPath API: Origin not allowed');
        return {
          success: false,
          error: 'Access forbidden. Please contact your administrator.',
        };
      }
      
      throw new Error(result.error || `API returned ${response.status}`);
    }

    // Log rate limit info for monitoring
    const remaining = response.headers.get('X-RateLimit-Remaining');
    const reset = response.headers.get('X-RateLimit-Reset');
    if (remaining !== null) {
      console.log(`📊 Rate limit: ${remaining} requests remaining (resets at ${reset})`);
    }

    console.log('✅ LightPath pricing fetched:', result.data);

    return result;
  } catch (error) {
    console.error('❌ Failed to fetch LightPath pricing:', error);
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch pricing',
    };
  }
}

/**
 * Convert LightPath payment schedule to contract Payment[] format
 */
export function convertLightPathPayments(
  apiPayments: LightPathPaymentMilestone[]
): Array<{
  id: string;
  description: string;
  amount: number;
  dueDate?: string;
  milestone?: number;
}> {
  return apiPayments.map((payment) => ({
    id: crypto.randomUUID(),
    description: payment.description,
    amount: payment.amount,
    milestone: payment.milestone,
  }));
}

